import { Fragment } from "react";
type Range = [start: number, end: number];
function editDistance(a: string, b: string): number {
  const row = Array.from({
    length: b.length + 1
  }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diagonal = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const above = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
      diagonal = above;
    }
  }
  return row[b.length];
}

/**
 * Where each word of a search sits in `text`, picked the way patient search
 * ranks: a whole word, then the start of a word, then anywhere. A word the API
 * matched with a typo ("Mentuk") marks the closest whole word instead, within
 * the same edit budget the API allows. Empty when nothing lines up, such as a
 * phone or email search.
 */
export function searchMatchRanges(text: string, search: string): Range[] {
  const lower = text.toLowerCase();
  const nameWords = Array.from(lower.matchAll(/[^\s-]+/g), m => {
    const start = m.index ?? 0;
    return {
      word: m[0],
      start,
      end: start + m[0].length
    };
  });
  const ranges: Range[] = [];
  const searchWords = search.toLowerCase().split(/\s+/).filter(Boolean);
  for (const word of searchWords.slice(0, 4)) {
    const whole = nameWords.find(w => w.word === word);
    const prefix = nameWords.find(w => w.word.startsWith(word));
    const at = lower.indexOf(word);
    if (whole) ranges.push([whole.start, whole.end]);else if (prefix) ranges.push([prefix.start, prefix.start + word.length]);else if (at >= 0) ranges.push([at, at + word.length]);else if (!/[0-9@]/.test(word)) {
      const budget = word.length >= 6 ? 2 : word.length >= 4 ? 1 : 0;
      const closest = nameWords.filter(w => Math.abs(w.word.length - word.length) <= budget).map(w => ({
        ...w,
        distance: editDistance(w.word, word)
      })).sort((a, b) => a.distance - b.distance)[0];
      if (closest && closest.distance <= budget) ranges.push([closest.start, closest.end]);
    }
  }
  return ranges;
}

/** `text` with the characters inside `ranges` bolded. */
export function SearchMatchText({
  text,
  ranges
}: {
  text: string;
  ranges: Range[];
}) {
  if (ranges.length === 0) return text;
  const hit = Array.from(text, (_, i) => ranges.some(([start, end]) => i >= start && i < end));
  const parts: {
    text: string;
    hit: boolean;
  }[] = [];
  hit.forEach((isHit, i) => {
    const last = parts[parts.length - 1];
    if (last?.hit === isHit) last.text += text[i];else parts.push({
      text: text[i],
      hit: isHit
    });
  });
  return parts.map((part, i) => part.hit ? <span key={i} className="font-semibold">
        {part.text}
      </span> : <Fragment key={i}>{part.text}</Fragment>);
}